import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import theme from '../../styles/theme';
import { useResponsive } from '../../hooks/useResponsive';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

/**
 * Thin sticky footer with copyright notice and globe language dropdown.
 */
const Footer = () => {
  const { i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const styles = {
    footer: {
      backgroundColor: theme.colors.primary.main,
      padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.xl}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? theme.spacing.sm : theme.spacing.md,
      flexShrink: 0,
    },
    notice: {
      color: 'rgba(255,255,255,0.7)',
      fontSize: theme.typography.fontSize.xs,
      letterSpacing: '0.3px',
      lineHeight: theme.typography.lineHeight.normal,
    },
    langWrapper: {
      position: 'relative',
    },
    globeButton: {
      background: 'transparent',
      border: 'none',
      color: 'rgba(255,255,255,0.8)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSize.xs,
      transition: theme.transitions.fast,
      fontFamily: theme.typography.fontFamily.primary,
      minHeight: '40px',
    },
    dropdown: {
      position: 'absolute',
      bottom: '100%',
      right: 0,
      marginBottom: '6px',
      backgroundColor: theme.colors.background.paper,
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.shadows.lg,
      overflow: 'hidden',
      minWidth: '120px',
      zIndex: 1000,
    },
    option: (isActive) => ({
      display: 'block',
      width: '100%',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      border: 'none',
      background: isActive ? theme.colors.primary.light : 'transparent',
      color: isActive ? theme.colors.primary.main : theme.colors.text.primary,
      fontWeight: isActive
        ? theme.typography.fontWeight.semibold
        : theme.typography.fontWeight.normal,
      fontSize: theme.typography.fontSize.sm,
      textAlign: 'left',
      cursor: 'pointer',
      transition: theme.transitions.fast,
      fontFamily: theme.typography.fontFamily.primary,
    }),
  };

  return (
    <footer style={styles.footer}>
      <span style={styles.notice}>© {new Date().getFullYear()} Wag and Roll. All rights reserved.</span>

      <div style={styles.langWrapper} ref={dropdownRef}>
        <button
          style={styles.globeButton}
          onClick={() => setOpen((prev) => !prev)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          aria-label="Change language"
        >
          <Globe size={15} />
          {currentLang.label}
        </button>

        {open && (
          <div style={styles.dropdown}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                style={styles.option(lang.code === i18n.language)}
                onClick={() => changeLanguage(lang.code)}
                onMouseEnter={(e) => {
                  if (lang.code !== i18n.language) {
                    e.currentTarget.style.backgroundColor = theme.colors.background.elevated;
                  }
                }}
                onMouseLeave={(e) => {
                  if (lang.code !== i18n.language) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {lang.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
